/**
 * <TutorLiveRail> — une colonne du live du masteur, repliée (molecule).
 *
 * Les chevrons de l'ancien focus (`TutorFocusLayoutM`), repris au round 3 (Wajih : leur retrait était
 * une erreur) et de la colonne repliable de la V2 : la pellicule et le fil se replient pour élargir le
 * centre — pendant une présentation surtout, où le centre est ce que voient les élèves. Repliée, une
 * colonne garde l'essentiel en visages : les élèves (le regardé entouré) à gauche, ce que le fil
 * demande à droite (le signe de chaque entrée, l'urgente teintée). Le chevron qui la rouvre est posé par la page, à
 * la place exacte de celui qui l'a repliée (`COLUMN_TOGGLE`) : le rail commence dessous. Les largeurs sont celles de
 * l'ancien focus : 40 à gauche, 80 à droite (`RAIL_WIDTH`).
 *
 * Repliée, la colonne de droite dit aussi CE QUI ATTEND : le compte de « À traiter » sous le chevron, rouge dès qu'un
 * signal d'élève attend, et le visage de l'élève concerné à pastille rouge (Wajih, 05/11 : « en mode réduit on ne voit
 * vraiment pas d'alerte à droite »). Un signal qui arrive entre d'un coup (le visage grossit en fondu, `standard`) ;
 * en fondu seul quand le système demande moins d'animation.
 *
 * Contrôlée : ce qui est replié vit dans la page, qui le reçoit en props.
 */
import type React from "react"
import { Animated, Pressable, View } from "react-native"
import { StyleSheet } from "react-native-unistyles"

import { Badge } from "../../atoms/Badge"
import { ScrollView } from "../../atoms/ScrollView"
import { useFadeIn } from "../../hooks/useFadeIn"
import { cappedCount } from "../../utils/cappedCount"
import { type AvatarSign, SignedAvatar } from "../SignedAvatar"

/** Une ligne repliée : un visage, et ce qui le marque. */
export type TutorLiveRailItem = {
    id: string
    firstname: string
    avatarUrl?: string
    /** Le regardé : entouré de l'univers. */
    selected?: boolean
    /** Un signe posé sur le visage (le signal d'une entrée du fil), et sa pastille. */
    sign?: AvatarSign
    /** Ce que dit la ligne au lecteur d'écran : « Karim lève la main ». */
    label: string
    /** L'identité de ce qui marque le visage (un signal) : un signal nouveau rejoue l'entrée. Défaut : `id`. */
    entryKey?: string
}

export type TutorLiveRailProps = {
    items: TutorLiveRailItem[]
    onPressItem: (id: string) => void
    /** Déplier la colonne : le compte le fait aussi. */
    onExpand: () => void
    /** Le compte de ce qui attend, sous le chevron : rouge quand `urgent`. Absent ou 0 : rien. */
    count?: { value: number; urgent: boolean; label: string }
    testID?: string
}

/** Les largeurs repliées de l'ancien focus (`collapsedLeftWidth`, `collapsedRightWidth`). */
export const RAIL_WIDTH = { left: 40, right: 80 } as const

const FACE = 28

export function TutorLiveRail(props: TutorLiveRailProps) {
    const { items, onPressItem, onExpand, count, testID } = props
    return (
        <View style={styles.root} testID={testID}>
            {count != null && count.value > 0 && (
                <Pressable onPress={onExpand} accessibilityRole="button" accessibilityLabel={count.label}>
                    <Badge text={cappedCount(count.value)} variant={count.urgent ? "danger" : "neutral"} />
                </Pressable>
            )}
            <ScrollView contentContainerStyle={styles.list} showsVerticalScrollIndicator={false}>
                {items.map((item) => (
                    <Pressable
                        key={item.id}
                        onPress={() => onPressItem(item.id)}
                        accessibilityRole="button"
                        accessibilityState={{ selected: item.selected === true }}
                        accessibilityLabel={item.label}
                        style={styles.item}
                    >
                        <RailEntry key={item.entryKey ?? item.id}>
                            <SignedAvatar
                                uri={item.avatarUrl}
                                name={item.firstname}
                                size={FACE}
                                sign={item.sign}
                                selected={item.selected}
                            />
                        </RailEntry>
                    </Pressable>
                ))}
            </ScrollView>
        </View>
    )
}

/**
 * L'entrée d'une marque nouvelle (un signal nouveau la remonte, `key`) : elle grossit en fondu (`useFadeIn`) ;
 * immobile en mouvement réduit. Le compte, un chiffre, ne bouge jamais.
 */
function RailEntry(props: { children: React.ReactNode }) {
    const opacity = useFadeIn(true)
    const scale = opacity.interpolate({ inputRange: [0, 1], outputRange: [0.6, 1] })
    return <Animated.View style={{ opacity, transform: [{ scale }] }}>{props.children}</Animated.View>
}

const styles = StyleSheet.create((theme) => ({
    root: { flex: 1, minHeight: 0, alignItems: "center" as const, gap: theme.spacing["2"] },
    list: { alignItems: "center" as const, gap: theme.spacing["2"], paddingVertical: theme.spacing["1"] },
    item: { width: FACE + 6, height: FACE + 6, alignItems: "center" as const, justifyContent: "center" as const },
}))
